import { useMemo, useState } from 'react';

import { GlobalFilterFieldKey } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';

interface Props {
    componentKey: GlobalFilterFieldKey;
}
export interface FilterOption {
    [key: string]: { label: string; isSelected: boolean };
}

export const MORE_FILTERS_BUTTON_KEYS = ['battlecardsRowsBoard', 'activitiesPivotTableView'];

export const dropdownOptionsDetails: FilterOption = {
    milestoneFragnet: { label: 'Activity Fragnet', isSelected: false },
    activityForwardFragnet: { label: 'Activity Forward Fragnet', isSelected: false },
    baselineStartDate: { label: 'Baseline Start Date', isSelected: false },
    baselineFinishDate: { label: 'Baseline Finish Date', isSelected: false },
    cardType: { label: 'Card Type', isSelected: false },
    cluster: { label: 'Cluster', isSelected: false },
    completionPlanSegment: { label: 'Completion Plan Segment', isSelected: false },
    duration: { label: 'Duration', isSelected: false },
    float: { label: 'Float', isSelected: false },
    hasAttachments: { label: 'Has Attachments', isSelected: false },
    hasComments: { label: 'Has Comments', isSelected: false },
    lagLead: { label: 'Lag/Lead', isSelected: false },
    relatedActivity: { label: 'Related Activity', isSelected: false },
    resourceName: { label: 'Resource Name', isSelected: false },
    resourceType: { label: 'Resource Type', isSelected: false },
    startDate: { label: 'Start Date', isSelected: false },
    submittedCompletion: { label: 'Submitted Completion', isSelected: false },
    trackedActivity: { label: 'Tracked Activity', isSelected: false },
    updatedBy: { label: 'Updated By', isSelected: false },
    wbsLevel: { label: 'WBS Level', isSelected: false },
};

const componentKeyToComponentMoreOptions = {
    battlecardsRowsBoard: [
        GlobalFilterFieldKey.milestoneFragnet,
        GlobalFilterFieldKey.activityForwardFragnet,
        GlobalFilterFieldKey.baselineStartDate,
        GlobalFilterFieldKey.baselineFinishDate,
        GlobalFilterFieldKey.cardType,
        GlobalFilterFieldKey.cluster,
        GlobalFilterFieldKey.completionPlanSegment,
        GlobalFilterFieldKey.duration,
        GlobalFilterFieldKey.float,
        GlobalFilterFieldKey.hasAttachments,
        GlobalFilterFieldKey.hasComments,
        GlobalFilterFieldKey.lagLead,
        GlobalFilterFieldKey.relatedActivity,
        GlobalFilterFieldKey.resourceName,
        GlobalFilterFieldKey.resourceType,
        GlobalFilterFieldKey.startDate,
        GlobalFilterFieldKey.submittedCompletion,
        GlobalFilterFieldKey.trackedActivity,
        GlobalFilterFieldKey.updatedBy,
        GlobalFilterFieldKey.wbsLevel,
    ],
    activitiesPivotTableView: [
        GlobalFilterFieldKey.milestoneFragnet,
        GlobalFilterFieldKey.activityForwardFragnet,
        GlobalFilterFieldKey.baselineStartDate,
        GlobalFilterFieldKey.baselineFinishDate,
        GlobalFilterFieldKey.cardType,
        GlobalFilterFieldKey.cluster,
        GlobalFilterFieldKey.completionPlanSegment,
        GlobalFilterFieldKey.duration,
        GlobalFilterFieldKey.float,
        GlobalFilterFieldKey.hasAttachments,
        GlobalFilterFieldKey.hasComments,
        GlobalFilterFieldKey.lagLead,
        GlobalFilterFieldKey.relatedActivity,
        GlobalFilterFieldKey.resourceName,
        GlobalFilterFieldKey.resourceType,
        GlobalFilterFieldKey.startDate,
        GlobalFilterFieldKey.submittedCompletion,
        GlobalFilterFieldKey.trackedActivity,
        GlobalFilterFieldKey.updatedBy,
        GlobalFilterFieldKey.wbsLevel,
    ],
};

const hasFilterValue = (value: unknown): boolean => {
    if (value === undefined) {
        return false;
    }
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    return true;
};

const getInitialDropdownItemsDetails = (dropdownFiltersKeys: string[]): FilterOption => {
    return dropdownFiltersKeys.reduce<FilterOption>((acc, key) => {
        if (dropdownOptionsDetails[key]) {
            acc[key] = { ...dropdownOptionsDetails[key] };
        }
        return acc;
    }, {});
};

export const useGetDropdownOptions = ({ componentKey }: Props) => {
    const dropdownOptions: GlobalFilterFieldKey[] = useMemo(() => {
        if (MORE_FILTERS_BUTTON_KEYS.includes(componentKey)) {
            return componentKeyToComponentMoreOptions[componentKey];
        }
        return [];
    }, [componentKey]);

    const initialDropdownFiltersOptionsState = useMemo(() => {
        const defaultDropdownOptions = getInitialDropdownItemsDetails(dropdownOptions);

        return Object.keys(defaultDropdownOptions).reduce<FilterOption>((acc, key) => {
            const defaultDropdownOption = defaultDropdownOptions[key];
            const filterValue = dropdownOptions[key];
            acc[key] = hasFilterValue(filterValue)
                ? { ...defaultDropdownOption, isSelected: true }
                : { ...defaultDropdownOption };
            return acc;
        }, {});
    }, [dropdownOptions]);

    const [dropdownFiltersOptions, setDropdownFiltersOptions] = useState<FilterOption>(
        initialDropdownFiltersOptionsState,
    );

    return {
        dropdownFiltersOptions,
        setDropdownFiltersOptions,
    };
};
